import {
  Children,
  useCallback,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { MobileMenuNavigationContext } from './contexts/MobileMenuNavigationContext.js';
import { MobileSubTrigger } from './MobileSubTrigger.js';
import { SubContent } from './SubContent.js';
import { type MenuSubTriggerProps, SubTrigger } from './SubTrigger.js';
import { type MenuSubProps } from './types.js';
import { isSlot } from '../../core/utils/is-slot.js';

type MobileSubSlots = {
  trigger: ReactElement<MenuSubTriggerProps> | null;
  contentChildren: ReactNode;
};

function getMobileSubSlots(children: ReactNode): MobileSubSlots {
  const slots: MobileSubSlots = { trigger: null, contentChildren: null };

  Children.forEach(children, (child) => {
    if (isSlot(child, SubTrigger)) {
      slots.trigger = child;
    } else if (isSlot(child, SubContent)) {
      slots.contentChildren = (
        child as ReactElement<{ children?: ReactNode }>
      ).props.children;
    }
  });

  return slots;
}

/**
 * Mobile-only Sub that drills into nested content instead of opening a popup.
 * @internal
 */
export const MobileSub = (props: MenuSubProps) => {
  const { children } = props;
  const { push } = useContext(MobileMenuNavigationContext);
  const slots = useMemo(() => getMobileSubSlots(children), [children]);

  const triggerProps: Partial<MenuSubTriggerProps> = slots.trigger?.props ?? {};

  const handleDrillDown = useCallback(() => {
    push({
      title: triggerProps.children,
      content: slots.contentChildren,
    });
  }, [push, triggerProps.children, slots.contentChildren]);

  return (
    <MobileSubTrigger
      onDrillDown={handleDrillDown}
      disabled={triggerProps.disabled}
      aria-label={triggerProps['aria-label']}
      aria-labelledby={triggerProps['aria-labelledby']}
      aria-describedby={triggerProps['aria-describedby']}
      aria-details={triggerProps['aria-details']}
    >
      {triggerProps.children}
    </MobileSubTrigger>
  );
};

(MobileSub as typeof MobileSub & { displayName: string }).displayName =
  'Menu.Sub';
